import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Compass, AlertCircle, Gift, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import GoogleLoginButton from "../components/GoogleLoginButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", nickname: "", email: "", password: "", referral_code: (params.get("ref") || "").toUpperCase() });
  const [refStatus, setRefStatus] = useState(null); // null | {valid, inviter_name} | {valid:false}
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // validate referral code as user types (debounced)
  useEffect(() => {
    const code = form.referral_code?.trim().toUpperCase();
    if (!code) { setRefStatus(null); return; }
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.post(`/referral/validate`, { code });
        setRefStatus(data);
      } catch {
        setRefStatus({ valid: false });
      }
    }, 350);
    return () => clearTimeout(tid);
  }, [form.referral_code]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.nickname.trim(), form.referral_code?.trim() || null);
      toast.success("Account creato!");
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment-grain flex items-center justify-center p-4">
      <Card className="card-treasure p-8 w-full max-w-md animate-bounce-in">
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-3xl bg-gold flex items-center justify-center shadow-tactile mb-3">
            <Compass className="text-ink-900" size={32} strokeWidth={2.6} />
          </div>
          <h1 className="text-3xl font-display font-extrabold text-ink-900">Unisciti all'avventura</h1>
          <p className="text-ink-500 text-sm">Crea il tuo account esploratore</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-ink-800 font-bold">Nome</Label>
            <Input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="h-12 rounded-2xl border-outline" data-testid="register-name-input" />
          </div>
          <div>
            <Label className="text-ink-800 font-bold">Nickname pubblico</Label>
            <Input
              required
              minLength={2}
              maxLength={24}
              value={form.nickname}
              onChange={(e)=>setForm({...form,nickname:e.target.value})}
              placeholder="Es. EsploratoreMax"
              className="h-12 rounded-2xl border-outline"
              data-testid="register-nickname-input"
            />
            <p className="text-[11px] text-ink-500 mt-1">
              È il nome mostrato agli altri nelle classifiche. Il tuo nome vero resta privato.
            </p>
          </div>
          <div>
            <Label className="text-ink-800 font-bold">Email</Label>
            <Input type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="h-12 rounded-2xl border-outline" data-testid="register-email-input" />
          </div>
          <div>
            <Label className="text-ink-800 font-bold">Password (min 6)</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="h-12 rounded-2xl border-outline" data-testid="register-password-input" />
          </div>
          <div>
            <Label className="text-ink-800 font-bold flex items-center gap-1"><Gift size={14}/> Codice invito (opzionale)</Label>
            <Input
              value={form.referral_code}
              onChange={(e)=>setForm({...form,referral_code:e.target.value.toUpperCase()})}
              placeholder="Es. LUCA01"
              maxLength={6}
              className="h-12 rounded-2xl border-outline uppercase tracking-widest font-display font-bold"
              data-testid="register-referral-input"
            />
            {refStatus && form.referral_code && (
              refStatus.valid ? (
                <div className="mt-1 flex items-center gap-2 text-sm text-forest font-bold" data-testid="referral-valid">
                  <CheckCircle2 size={14}/> Invitato da <span className="font-extrabold">{refStatus.inviter_name}</span> · riceverai +100 pt al primo percorso!
                </div>
              ) : (
                <div className="mt-1 text-sm text-compass font-bold" data-testid="referral-invalid">Codice non valido</div>
              )
            )}
          </div>
          {err && <div className="flex items-center gap-2 text-sm text-compass bg-red-50 p-3 rounded-xl border border-red-200"><AlertCircle size={16}/>{err}</div>}
          <button type="submit" disabled={loading} className="btn-tactile w-full" data-testid="register-submit-btn">
            {loading ? "Creazione..." : "Crea account"}
          </button>
        </form>
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-outline" />
          <span className="text-xs uppercase tracking-widest text-ink-500 font-bold">oppure</span>
          <div className="flex-1 h-px bg-outline" />
        </div>
        <GoogleLoginButton testId="register-google-btn" label="Registrati con Google" />
        <div className="mt-4 text-center text-sm text-ink-700">
          Hai già un account? <Link to="/login" className="font-bold text-ocean" data-testid="link-login">Accedi</Link>
        </div>
      </Card>
    </div>
  );
}
